import type {CSSResultArray} from 'lit';
import {html} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import styles from './style.scss';
import type FlyRadio from '#lit/components/base/radio/index.js';
import {
  COMPONENT_TAG as FLY_RADIO_COMPONENT_TAG,
  CUSTOM_EVENT as FLY_RADIO_CUSTOM_EVENT,
} from '#lit/components/base/radio/index.js';

import type FlyRadioCard from '#lit/components/base/radio_card/index.js';
import {COMPONENT_TAG as FLY_RADIO_CARD_COMPONENT_TAG} from '#lit/components/base/radio_card/index.js';

import type {ClassInfo} from 'lit/directives/class-map.js';
import {classMap} from 'lit/directives/class-map.js';
import {isNonEmptyString} from '#lit/utils/index.js';
import type {FormFieldCustomValidationReport} from '#lit/components/abstract/form_field/index.js';
import {isHTMLElement} from '#lit/utils/dom.js';
import {when} from 'lit/directives/when.js';

export const COMPONENT_TAG = 'fly-radio-fieldset';

type RadioFieldNode = FlyRadio | FlyRadioCard;

export interface CustomChangeEvent {
  readonly tag: FLY_RADIO_CUSTOM_EVENT;
  readonly key: string;
  readonly name: string;
  readonly label: string;
  readonly value: string;
  readonly required: boolean;
  readonly validity: FormFieldCustomValidationReport;
  readonly disabled: boolean;
  readonly readonly: boolean;
  readonly touched: boolean;
  readonly visited: boolean;
  readonly headless: boolean;
  readonly initial: string;
  $nodes: Set<RadioFieldNode>;
}

@customElement(COMPONENT_TAG)
export default class FlyRadioFieldset extends FlywheelComponent {
  @property({type: String})
  key = `${new Date().getTime()}-${Math.random()}`;

  @property({type: String})
  label = '';

  @property({type: String})
  hint? = '';

  @property({type: String})
  name = '';

  @property({type: String})
  initial = '';

  @property({type: String})
  private value = '';

  @property({type: Boolean})
  active = false;

  @property({type: Boolean, reflect: true})
  disabled = false;

  @property({type: Boolean, reflect: true})
  headless = false;

  @property({type: Number})
  col = 1;

  @property({type: Boolean})
  required = false;

  @property({type: Boolean})
  readonly = false;

  @property({type: Boolean})
  visited = false;

  @property({type: Boolean})
  touched = false;

  @property({type: Boolean})
  hideLabel = false;

  private $nodes: Set<RadioFieldNode> = new Set<RadioFieldNode>();
  private validChildrenInputTagNames = [
    FLY_RADIO_COMPONENT_TAG,
    FLY_RADIO_CARD_COMPONENT_TAG,
  ];

  static get styles(): CSSResultArray {
    return [styles];
  }

  firstUpdated() {
    this.initialize();
  }

  rendered() {
    this.requestUpdate();
  }

  get validity(): FormFieldCustomValidationReport {
    if (this.touched) {
      const valid = isNonEmptyString(this.value) ? true : !this.required;
      return {state: valid, message: valid ? [] : ['Required']};
    }

    return {
      state: true,
      message: [] as ReadonlyArray<string>,
    };
  }

  initialize(value: string = this.initial) {
    this.setPropOnNodes('checked', false);
    this.touched = false;
    this.visited = false;
    this.setPropOnNodes('invalid', !this.validity.state);
    this.value = value;
  }

  private setPropOnNodes(
    prop: 'readonly' | 'disabled' | 'invalid' | 'checked',
    value: boolean,
    $nodes: RadioFieldNode[] = Array.from(this.$nodes.values())
  ) {
    for (const $node of $nodes) {
      $node[prop] = value;
      $node.requestUpdate();
    }
    return;
  }

  private queryInputChildren(node: Node): HTMLElement[] {
    if (!isHTMLElement(node)) {
      return [];
    }

    if (this.validChildrenInputTagNames.includes(node.localName)) {
      return [node];
    }

    const inputsSelector = this.validChildrenInputTagNames.join(', ');
    const children = node.querySelectorAll<HTMLElement>(inputsSelector);

    return Array.from(children);
  }

  private onSlotChange(evt: Event) {
    const $slotElements = (evt.currentTarget as HTMLSlotElement).assignedNodes({
      flatten: true,
    });

    const elementsArray = $slotElements
      .flatMap(element => this.queryInputChildren(element))
      .filter(Boolean);

    this.registerNodes(elementsArray);
  }

  registerNodes($elements: HTMLElement[]) {
    if (!Array.isArray($elements) || !$elements.length) return;
    const $updatedNodes = $elements.reduce(
      ($nodes: Set<RadioFieldNode>, $el: HTMLElement) => {
        const $radio = $el as RadioFieldNode;
        if (
          ![FLY_RADIO_COMPONENT_TAG, FLY_RADIO_CARD_COMPONENT_TAG].includes(
            $radio.localName
          ) ||
          $nodes.has($radio)
        ) {
          return $nodes;
        }

        const onchange = () => {
          this.visited = this.touched = true;

          if ($radio.checked) {
            for (const $ of this.$nodes) {
              if ($.isSameNode($radio)) continue;
              $.checked = false;
            }
            this.value = $radio.value;
          } else if ($radio.value === this.value) {
            this.value = '';
          }

          if (!$radio.readonly) {
            $radio.disabled = this.disabled;
          }

          this.setPropOnNodes('invalid', !this.validity.state);

          this.dispatchChangeEvent(FLY_RADIO_CUSTOM_EVENT.CHANGE);
        };

        $radio.addEventListener(FLY_RADIO_CUSTOM_EVENT.FOCUS, () => {
          this.visited = true;
          this.dispatchEvent(new CustomEvent(FLY_RADIO_CUSTOM_EVENT.FOCUS));
        });

        $radio.addEventListener(FLY_RADIO_CUSTOM_EVENT.BLUR, () => {
          this.dispatchEvent(new CustomEvent(FLY_RADIO_CUSTOM_EVENT.BLUR));
          onchange();
        });

        $radio.addEventListener(FLY_RADIO_CUSTOM_EVENT.CHANGE, onchange);

        $radio.disabled = this.disabled;
        if ($radio.checked && !isNonEmptyString(this.value)) {
          this.value = $radio.value;
        } else if (
          $radio.checked &&
          isNonEmptyString(this.value) &&
          $radio.value !== this.value
        ) {
          $radio.checked = false;
        } else if (
          !$radio.checked &&
          isNonEmptyString(this.value) &&
          $radio.value === this.value
        ) {
          $radio.checked = true;
        }

        $nodes.add($radio);

        return $nodes;
      },
      new Set<RadioFieldNode>(this.$nodes)
    );

    this.$nodes = $updatedNodes;
  }

  detachNodes($elements: HTMLElement[]) {
    for (const $el of $elements) {
      const $radio = $el as RadioFieldNode;
      if (
        ![FLY_RADIO_COMPONENT_TAG, FLY_RADIO_CARD_COMPONENT_TAG].includes(
          $radio.localName
        ) ||
        !this.$nodes.has($radio)
      ) {
        continue;
      }
      this.$nodes.delete($radio);
      if (this.value === $radio.value) this.value = '';
    }
  }

  private dispatchChangeEvent(tag: FLY_RADIO_CUSTOM_EVENT) {
    const evt = {
      tag,
      name: this.name,
      value: this.value,
      label: this.label,
      key: this.key,
      disabled: this.disabled,
      validity: this.validity,
      readonly: this.readonly,
      required: this.required,
      touched: this.touched,
      visited: this.visited,
      initial: this.initial,
      headless: this.headless,
      $nodes: this.$nodes,
    };

    this.dispatchEvent(
      new CustomEvent<CustomChangeEvent>(FLY_RADIO_CUSTOM_EVENT.CHANGE, {
        detail: evt,
      })
    );

    window.dispatchEvent(
      new CustomEvent<CustomChangeEvent>(
        `${COMPONENT_TAG}:${FLY_RADIO_CUSTOM_EVENT.CHANGE}`,
        {
          detail: evt,
        }
      )
    );
  }

  updated(changedProperties: Map<string, unknown>) {
    changedProperties.forEach((oldValue, propName) => {
      switch (propName) {
        case 'disabled': {
          if (oldValue === this.disabled) return;
          this.setPropOnNodes('disabled', this.disabled);
          return;
        }

        case 'readonly': {
          if (oldValue === this.readonly) return;
          this.setPropOnNodes('readonly', this.readonly);
          return;
        }

        case 'value': {
          if (oldValue === this.value) return;
          this.dispatchChangeEvent(FLY_RADIO_CUSTOM_EVENT.CHANGE);
          return;
        }

        default: {
          return;
        }
      }
    });
  }

  get computedClasses(): Record<string, ClassInfo> {
    return {
      container: {
        'fly-radio-fieldset': true,
        'fly-radio-fieldset--headless': this.headless,
      },
      label: {
        'fly-radio-fieldset__label': true,
        'fly-radio-fieldset__label--hidden':
          !isNonEmptyString(this.label) || this.hideLabel,
      },
      content: {
        'fly-radio-fieldset__content': true,
      },
    };
  }

  render() {
    return html`
      <div
        id="fly-radio-fieldset-${this.key}"
        class=${classMap(this.computedClasses.container)}
      >
        <div class=${classMap(this.computedClasses.label)}>
          ${this.label}
          ${when(
            this.hint,
            () => html`
              <fly-tooltip content="${this.hint!}">
                <fly-icon .icon=${'info_outlined'}></fly-icon>
              </fly-tooltip>
            `
          )}
        </div>
        <div
          class=${classMap(this.computedClasses.content)}
          style="--fly-radio-fieldset-grid-col: ${this.col}"
        >
          <slot @slotchange=${this.onSlotChange.bind(this)}></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_TAG]: FlyRadioFieldset;
  }
}
